import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../apis/store";
import { useCallback, useEffect, useMemo, useState } from "react";
import PaginationComponent from "../../../Components/biblio/PaginationComponent";
import { useTranslation } from "react-i18next";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { HiOutlineCalendarDateRange } from "react-icons/hi2";
import { MdOutlineReceiptLong } from "react-icons/md";
import MyInvoicesDetails from "./MyInvoicesDetails";
import ProfileTabState from "./ProfileTabState";
import { toGetMyInvoices } from "../../../apis/actions/appointments.actions";
import { formatInvoiceDateDisplay } from "../../../utils/invoiceDisplay";

export const MyInvoices = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const language = i18n.language;
  const isRtl = language.startsWith("ar");
  const titleFont = isRtl ? " ac--font-expo" : "";
  const itemPerPage = 4;
  const [currentPage, setActive] = useState<number>(1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<any>(null);

  const handleOpenModal = useCallback((data: any) => {
    setSelectedData(data);
    setShowModal(true);
  }, []);

  const { myInvoices } = useSelector((state: RootState) => state?.appointmens);
  const status = myInvoices?.status ?? "idle";
  const list = myInvoices?.list ?? [];

  useEffect(() => {
    if (status !== "idle") return;
    dispatch(toGetMyInvoices());
  }, [dispatch, status]);

  const loading = status === "idle" || status === "loading";
  const empty =
    (status === "succedded" || status === "rejected") && list.length === 0;

  const pageItems = useMemo(
    () =>
      list.slice(
        itemPerPage * (currentPage - 1),
        itemPerPage * currentPage
      ),
    [list, currentPage]
  );

  const DetailArrow = isRtl ? IoIosArrowBack : IoIosArrowForward;

  return (
    <>
      <ProfileTabState loading={loading} empty={empty} emptyKey="invoices">
        <div className="ac-order-list ac-order-list--invoices">
          {pageItems.map((el: any) => (
            <article
              key={el.id ?? el.name}
              className="ac-order-card ac-order-card--invoice"
            >
              <div className="ac-order-card__main">
                <header className="ac-order-card__head">
                  <div className="ac-order-card__identity">
                    <p className={`ac-order-card__eyebrow${titleFont}`}>
                      {t("profile.invoice.invoiceNumber")}
                    </p>
                    <h3 className={`ac-order-card__ref${titleFont}`}>
                      {el.name}
                    </h3>
                  </div>
                </header>

                <dl className="ac-order-card__meta">
                  <div className="ac-order-card__meta-row">
                    <dt>
                      <HiOutlineCalendarDateRange
                        className="ac-order-card__meta-icon"
                        aria-hidden
                      />
                      <span className={titleFont}>
                        {t("profile.invoice.invoiceDate")}
                      </span>
                    </dt>
                    <dd className={titleFont}>
                      {formatInvoiceDateDisplay(el, language)}
                    </dd>
                  </div>
                  <div className="ac-order-card__meta-row">
                    <dt>
                      <MdOutlineReceiptLong
                        className="ac-order-card__meta-icon"
                        aria-hidden
                      />
                      <span className={titleFont}>
                        {t("profile.order.cart.total")}{" "}
                        <span className="ac-order-card__meta-vat">
                          ({t("profile.order.cart.vat")})
                        </span>
                      </span>
                    </dt>
                    <dd className={titleFont}>
                      {el?.amount_total != null && el?.amount_total !== "" ? (
                        <span className="ac-order-card__amount icon-saudi_riyal">
                          {el.amount_total}
                        </span>
                      ) : (
                        <span className="ac-order-card__meta-muted">—</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="ac-order-card__aside">
                {el?.amount_total != null && el?.amount_total !== "" ? (
                  <span
                    className={`ac-order-card__status ac-order-card__status--amount${titleFont}`}
                  >
                    <span className="ac-order-card__amount icon-saudi_riyal">
                      {el.amount_total}
                    </span>
                  </span>
                ) : null}
                <button
                  type="button"
                  className={`ac-order-card__detail-btn${titleFont}`}
                  onClick={() => handleOpenModal(el)}
                >
                  {t("profile.order.details")}
                  <DetailArrow aria-hidden />
                </button>
              </div>
            </article>
          ))}
        </div>

        {list.length > itemPerPage && (
          <div className="ac-order-list__pagination">
            <PaginationComponent
              total={list.length}
              itemPerPage={itemPerPage}
              active={currentPage}
              setActive={(nb) => {
                setActive(nb);
              }}
            />
          </div>
        )}
      </ProfileTabState>

      <MyInvoicesDetails
        type="invoice"
        showModal={showModal}
        setShowModal={setShowModal}
        selectedData={selectedData}
      />
    </>
  );
};
export default MyInvoices;
